import { styled } from '@uhg-abyss/web/tools/styled';
import React, { useState, useEffect } from 'react';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const CardTitle = styled('h2', {
  margin: 0,
  color: '$primary1',
});

const HelperText = styled('p', {
  margin: 0,
  fontWeight: 500,
  color: '$black',
});

const ThreeColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '16px',
  width: '100%',
});

const FILE_UPLOAD_CONTENT_WIDTH_CSS = { width: '100%', maxWidth: '380px' };
const FILE_UPLOAD_CSS = {
  'abyss-file-upload-drop-zone': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-input-file-list-list': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-upload-file-list-item': { wordBreak: 'break-word' },
};
const DOC360_UPLOAD_ERROR_ALERT_CSS = {
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
};

const MAX_FILE_NAME_LENGTH = 54; // 50 chars + 4 for .pdf extension
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const PASSWORD_PROTECTED_UPLOAD_MESSAGE =
  'Upload failed: This file is password-protected and cannot be processed. Please remove the password and try uploading again.';
const DOC360_UPLOAD_ERROR_MESSAGE =
  "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)";
const getUploadErrorMessage = (error) => {
  const messageCandidates = [
    error?.message,
    error?.data?.message,
    error?.response?.data?.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const faultCodeCandidates = [
    error?.faultCode,
    error?.data?.faultCode,
    error?.response?.data?.faultCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const isPasswordProtectedError =
    /password[ -]?protected|encrypted|cannot be processed/.test(
      messageCandidates
    ) ||
    /password|encrypt/.test(faultCodeCandidates) ||
    faultCodeCandidates.includes('doduee');

  if (isPasswordProtectedError) {
    return PASSWORD_PROTECTED_UPLOAD_MESSAGE;
  }

  return error?.message || DOC360_UPLOAD_ERROR_MESSAGE;
};
const FILE_UPLOAD_VALIDATE = {
  maxFiles: (files) => {
    return files && files.length > 1 ? 'Maximum 1 file allowed.' : true;
  },
  fileLength: (files) => {
    return files &&
      files.some((file) => file.name.length > MAX_FILE_NAME_LENGTH)
      ? 'File Name cannot exceed 50 characters in length.'
      : true;
  },
  fileSize: (files) => {
    return files && files.some((file) => file.size > MAX_FILE_SIZE_BYTES)
      ? 'File is larger than 5 MB.'
      : true;
  },
};

const FEDERAL_TAX_CLASSIFICATION_OPTIONS = [
  {
    label: 'Individual/sole proprietor or single-member LLC',
    value: 'Individual/sole proprietor or single-member LLC',
  },
  { label: 'C Corporation', value: 'C Corporation' },
  { label: 'S Corporation', value: 'S Corporation' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Trust/estate', value: 'Trust/estate' },
  { label: 'Other', value: 'Other' },
];

const FormRows = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
});

const FileRow = styled('div', {
  width: '100%',
  maxWidth: '460px',
  border: '1px solid $core.color.neutral.40',
  borderRadius: '8px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  backgroundColor: '$white',
});

const FileName = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$core.color.neutral.90',
  maxWidth: '460px',
  wordBreak: 'break-word',
});

const RemoveFileButton = styled('button', {
  border: 'none',
  background: 'transparent',
  color: '$core.color.neutral.70',
  width: '20px',
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  borderRadius: '2px',
  '&:hover': {
    color: '$core.color.neutral.90',
  },
});

export const W9Upload = () => {
  const abyssForm = useFormV2();
  const userInfo = useUser.state();
  const { trackingNumber, documentIds } = useTinRegistration.state();
  const { uploadProof, setDocumentId, setDocumentLoading } =
    useTinRegistration.actions();
  const [doc360UploadError, setDoc360UploadError] = useState('');
  const [visibleFileName, setVisibleFileName] = useState('');
  // V2FileUpload does not rebuild its visible internal file list from draft-restored model values.
  // Show this fallback row only for restored drafts; keep native Abyss row for fresh uploads.
  const [showRestoredFileRow, setShowRestoredFileRow] = useState(false);

  useEffect(() => {
    const value = abyssForm.getValues('w9form.fileName');
    const file = Array.isArray(value) ? value[0] : value;
    const nextFileName = file?.name || '';
    const hasRestoredDraftFile =
      !!nextFileName && !!documentIds['w9form.fileName'];
    setVisibleFileName(hasRestoredDraftFile ? nextFileName : '');
    setShowRestoredFileRow(hasRestoredDraftFile);
  }, []);

  const handleW9FileChange = () => {
    const uploadedValue = abyssForm.getValues('w9form.fileName');
    const uploadedFile = Array.isArray(uploadedValue)
      ? uploadedValue[0]
      : uploadedValue;

    setDoc360UploadError('');

    if (!uploadedFile) {
      setVisibleFileName('');
      setShowRestoredFileRow(false);
      setDocumentId({ id: 'w9form.fileName', value: undefined });
      return;
    }

    // If this is a restored draft file object, preserve its existing doc360Id.
    if (!(uploadedFile instanceof File)) {
      const restoredDocId =
        uploadedFile?.doc360Id || documentIds['w9form.fileName'];
      if (uploadedFile && uploadedFile.name && restoredDocId) {
        setVisibleFileName(uploadedFile.name);
        setShowRestoredFileRow(true);
        if (!documentIds['w9form.fileName']) {
          setDocumentId({ id: 'w9form.fileName', value: restoredDocId });
        }
        return;
      }
      // Otherwise, it's an error
      setDoc360UploadError(DOC360_UPLOAD_ERROR_MESSAGE);
      setDocumentId({ id: 'w9form.fileName', value: undefined });
      return;
    }

    // Clear the doc360Id since this is a new upload
    setVisibleFileName('');
    setShowRestoredFileRow(false);
    setDocumentId({ id: 'w9form.fileName', value: undefined });
    // Let Abyss uploader render the standard file row for fresh uploads.

    // Avoid API upload for invalid files so only field-level validation appears.
    const isInvalidW9File =
      uploadedFile.size > MAX_FILE_SIZE_BYTES ||
      uploadedFile.name.length > MAX_FILE_NAME_LENGTH;
    if (isInvalidW9File) {
      setDocumentLoading({ model: 'w9form.fileName', value: false });
      return;
    }

    const registration = abyssForm.getValues('searchRegistration');
    const reqBody = {
      billingTin: registration?.billingTin,
      requestId: registration?.requestId,
      fileType: 'W9',
      trackingNumber,
      file: uploadedFile,
      test: userInfo?.isCheckoutUser,
      model: 'w9form.fileName',
    };

    setDocumentLoading({ model: 'w9form.fileName', value: true });
    uploadProof(
      reqBody,
      (resp) => {
        setDocumentLoading({ model: 'w9form.fileName', value: false });
        if (resp?.doc360Id) {
          setDocumentId({ id: 'w9form.fileName', value: resp.doc360Id });
          return;
        }
        setDoc360UploadError(DOC360_UPLOAD_ERROR_MESSAGE);
        abyssForm.setValue('w9form.fileName', [], {
          touched: false,
          errors: { required: true },
        });
        setDocumentId({ id: 'w9form.fileName', value: undefined });
      },
      (error) => {
        setDocumentLoading({ model: 'w9form.fileName', value: false });
        setDoc360UploadError(getUploadErrorMessage(error));
        abyssForm.setValue('w9form.fileName', [], {
          touched: false,
          errors: { required: true },
        });
        setDocumentId({ id: 'w9form.fileName', value: undefined });
      }
    );
  };

  const handleRemoveVisibleFile = () => {
    const currentValue = abyssForm.getValues('w9form.fileName');
    const currentFile = Array.isArray(currentValue)
      ? currentValue[0]
      : currentValue;

    // If a fresh upload is currently selected, this button only removes the
    // restored draft fallback row and should not clear the active upload model.
    if (currentFile instanceof File) {
      setVisibleFileName('');
      setShowRestoredFileRow(false);
      setDoc360UploadError('');
      return;
    }

    abyssForm.setValue('w9form.fileName', [], {
      touched: false,
      errors: { required: true },
    });
    setVisibleFileName('');
    setShowRestoredFileRow(false);
    setDoc360UploadError('');
    setDocumentId({ id: 'w9form.fileName', value: undefined });
  };

  return (
    <V2Card>
      <SectionStack>
        <CardTitle>W-9 information</CardTitle>
        <HelperText>
          Make sure your W-9 is dated within the last 12 months to avoid delays.
        </HelperText>
        <FormRows>
          <ThreeColumnRow>
            <V2TextInput
              id="w9upload-name-field"
              model="w9form.name"
              label="Name"
              subText="As shown on your income tax return"
              maxLength={100}
              isClearable
              autoComplete="none"
              validators={{ required: 'Name is required.' }}
            />
            <V2TextInput
              id="w9form-businessName-field"
              model="w9form.businessName"
              label="Business name/disregard entity name"
              maxLength={100}
              isClearable
              autoComplete="none"
            />
            <V2SelectInput
              id="w9form-federal-tax-classification"
              model="w9form.federalTaxClassification"
              label="Federal tax classification"
              options={FEDERAL_TAX_CLASSIFICATION_OPTIONS}
              placeholder="Select One"
              isClearable
              validators={{ required: 'Please select one.' }}
            />
          </ThreeColumnRow>
          <V2FileUpload
            id="upload-w9form-input"
            model="w9form.fileName"
            label="Please upload your completed IRS Form W-9 (Request for Taxpayer Identification Number and Certification)"
            subText="Must be a PDF under 5MB and not password-protected."
            css={FILE_UPLOAD_CSS}
            variant="minimal"
            fileTypes={{ 'application/pdf': ['.pdf'] }}
            onChange={handleW9FileChange}
            validators={{
              required: 'W-9 form is required.',
              validate: FILE_UPLOAD_VALIDATE,
            }}
          />
          {showRestoredFileRow && visibleFileName && (
            <FileRow>
              <FileName>
                <span>{visibleFileName}</span>
              </FileName>
              <RemoveFileButton type="button" onClick={handleRemoveVisibleFile}>
                <IconSymbol icon="close" variant="outlined" size="16px" />
              </RemoveFileButton>
            </FileRow>
          )}
          {doc360UploadError && (
            <V2Alert
              variant="error"
              dismissible={false}
              inline
              css={DOC360_UPLOAD_ERROR_ALERT_CSS}
            >
              {doc360UploadError}
            </V2Alert>
          )}
        </FormRows>
      </SectionStack>
    </V2Card>
  );
};
